import { useClientPortalSubmit } from "@certn/api";
import {
  Container,
  DatePickerFormikField,
  Divider,
  Link,
  NumberFieldFormikField,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
  ProgressLoader,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useCallback, useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import {
  WorkdayIntegrationContext,
  AtsIntegration,
} from "../../WorkdayIntegrationContext";
import { WORKDAY_DEPLOYMENT_GUIDE_URL } from "../../constants";
import { WhatsThis } from "../../../components/WhatsThis";
import { WorkdayIntegrationStepLayout } from "../WorkdayIntegrationStepLayout";

export function TokensStep() {
  const { t } = useTranslation();
  const [authenticationError, setAuthenticationError] = useState(false);
  const {
    atsIntegrationData,
    setAtsIntegration,
    goToNextStep,
    setWorkdayConfig,
  } = useContext(WorkdayIntegrationContext);

  const validationSchema = object().shape({
    oauth_token_endpoint: string().required().label(t("OAuth token endpoint")),
    wsdl_endpoint: string().required().label(t("WSDL endpoint")),
    oauth_client_id: string().required().label(t("Client ID")),
    oauth_client_secret: string().required().label(t("Client secret")),
    oauth_refresh_token: string().required().label(t("Refresh token")),
  });

  const { submit } = useClientPortalSubmit(
    "/api/internal/client-portal/ats-integrations/workday/validate-outgoing-auth/",
    {
      onFailure: () => {
        setAuthenticationError(true);
      },
      onSuccess: (workdayConfig) => {
        setWorkdayConfig(workdayConfig);
        goToNextStep();
      },
    }
  );

  const handleSubmit = useCallback(
    async (values: AtsIntegration) => {
      setAuthenticationError(false);
      setAtsIntegration(values);
      await submit(values);
    },
    [setAtsIntegration, submit]
  );

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, values }) => {
        return (
          <WorkdayIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="lever-details-page-outermost-container"
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 1,
                      stepCount: 3,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", { integration: "Workday" })}
                </Text>
                <ProgressLoader floating value={33} />
                <Stack gap="space-2">
                  <Text style="global/heading-medium">
                    {t(
                      "Add your integration details here to set up the connection with Certn. These enable Certn to send information to Workday."
                    )}
                  </Text>

                  <SectionMessage type="info" icon="info">
                    <Trans>
                      For detailed instructions on setting up your Workday
                      integration, see our Workday{" "}
                      <Link
                        textColor="text-weak"
                        testID="deployment-guide"
                        href={WORKDAY_DEPLOYMENT_GUIDE_URL}
                        isNewTab={true}
                      >
                        Deployment Guide
                      </Link>
                      .
                    </Trans>
                  </SectionMessage>
                </Stack>
                <div className={styles.mainContentContainer}>
                  {authenticationError && (
                    <SectionMessage
                      type="danger"
                      sectionTitle={t("Authentication failed")}
                      icon="error"
                    >
                      <Trans>
                        Please check your credentials and try again.
                      </Trans>
                    </SectionMessage>
                  )}
                  <TextInputFormikField
                    name="oauth_token_endpoint"
                    label={t("OAuth token endpoint")}
                    testID="oauth-token-endpoint"
                    info={
                      <WhatsThis>
                        {t(
                          "This is the authentication access token for your Workday account. Paste the token URL from the ‘View API Clients’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    isRequired
                    isDisabled={isSubmitting}
                  />
                  <TextInputFormikField
                    name="wsdl_endpoint"
                    label={t("WSDL endpoint")}
                    testID="wsdl-endpoint"
                    info={
                      <WhatsThis>
                        {t(
                          "This is the WSDL for the Workday integrations. Paste the token URL from the ‘Public Webservices’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    isRequired
                    isDisabled={isSubmitting}
                  />
                  <Divider />
                  <Text style="global/heading-medium">
                    {t("Workday account authentication details")}
                  </Text>
                  <TextInputFormikField
                    name="oauth_client_id"
                    label={t("Client ID")}
                    testID="oauth-client-id"
                    info={
                      <WhatsThis>
                        {t(
                          "This is the Client ID from ‘API Clients for Integrations’ in Workday. Paste the client ID from the ‘View API Clients’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    isRequired
                    isDisabled={isSubmitting}
                  />
                  <TextInputFormikField
                    name="oauth_client_secret"
                    label={t("Client secret")}
                    testID="oauth-client-secret"
                    info={
                      <WhatsThis>
                        {t(
                          "This is the Client Secret from ‘API Clients for Integrations’ in Workday. Paste the secret from the ‘View API Clients’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    isRequired
                    isDisabled={isSubmitting}
                  />
                  <TextInputFormikField
                    name="oauth_refresh_token"
                    label={t("Refresh token")}
                    testID="oauth-refresh-token"
                    info={
                      <WhatsThis>
                        {t(
                          "This is the Refresh Token from ‘API Clients for Integrations’. Paste the token from the ‘View API Clients’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    isRequired
                    isDisabled={isSubmitting}
                  />
                  <DatePickerFormikField
                    info={
                      <WhatsThis>
                        {t(
                          "Leave blank if non-expiring. Otherwise use the ‘Expiration Time’ from ‘API Clients for Integrations’ in Workday."
                        )}
                      </WhatsThis>
                    }
                    label={t("Refresh token expiry")}
                    name="oauth_refresh_token_expiry"
                    testID="oauth-refresh-token-expiry"
                  />
                  <NumberFieldFormikField
                    name="oauth_refresh_token_lifetime"
                    label={t("Refresh token lifetime")}
                    testID="oauth-refresh-token-lifetime"
                    isRequired
                    isDisabled={isSubmitting}
                    info={
                      <WhatsThis>
                        {t("Set to 0 if using non-expiring refresh tokens.")}
                      </WhatsThis>
                    }
                  />
                </div>
              </Stack>
            </Container>
          </WorkdayIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
};
